import React, { Fragment, useEffect, useMemo, useState } from 'react';

import {
  Box,
  Button,
  Card,
  Group,
  NumberInput,
  ScrollArea,
  Select,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useViewportSize } from '@mantine/hooks';
import chroma from 'chroma-js';
import { useTranslation } from 'react-i18next';

import { type TrackPoint } from '../../types';
import { formatMetricLabel } from '../../utils/metricLabels';

import type { ScrollAreaProps } from '@mantine/core';

import './BinTablePanel.css';
import './themeInversion.css';

const RESERVED_METRIC_KEYS = new Set(['utc', 'lat', 'lon', 'globalIndex']);
const MIN_BIN_COUNT = 1;
const MAX_BIN_COUNT = 36;
const MAX_BOUNDARIES = 60;
const PANEL_MIN_WIDTH = 420;
const PANEL_MIN_HEIGHT = 360;
const DARK_CELL_TEXT_COLOR = '#0b2135';
const LIGHT_CELL_TEXT_COLOR = '#f8fafc';
const MIN_CELL_TEXT_CONTRAST = 4.5;

type BinMode = 'count' | 'boundaries';
type AggregationKey = 'mean' | 'median' | 'min' | 'max' | 'sum' | 'count';

type AxisConfig = {
  metric: string | null;
  mode: BinMode;
  binCount: number;
  boundariesText: string;
};

type AxisError = 'no-data' | 'not-enough-boundaries' | 'invalid-boundaries';

type BinRange = {
  start: number;
  end: number;
  inclusiveEnd: boolean;
};

type AxisComputation = {
  ranges: BinRange[];
  error: AxisError | null;
};

const AXIS_ERROR_NO_DATA: AxisError = 'no-data';
const AXIS_ERROR_NOT_ENOUGH: AxisError = 'not-enough-boundaries';
const AXIS_ERROR_INVALID: AxisError = 'invalid-boundaries';

type CellComputation = {
  value: number | null;
  count: number;
};

type AggregationDefinition = {
  labelKey: string;
  fallback: string;
  compute: (samples: number[], count: number) => number | null;
  needsValue: boolean;
};

const AGGREGATIONS: Record<AggregationKey, AggregationDefinition> = {
  mean: {
    labelKey: 'race.binTable.aggregations.mean',
    fallback: 'Average (mean)',
    compute: (samples) => {
      if (samples.length === 0) {
        return null;
      }
      const sum = samples.reduce((acc, value) => acc + value, 0);
      return sum / samples.length;
    },
    needsValue: true,
  },
  median: {
    labelKey: 'race.binTable.aggregations.median',
    fallback: 'Median',
    compute: (samples) => {
      if (samples.length === 0) {
        return null;
      }
      const sorted = [...samples].sort((a, b) => a - b);
      const middle = Math.floor(sorted.length / 2);
      if (sorted.length % 2 === 0) {
        return (sorted[middle - 1] + sorted[middle]) / 2;
      }
      return sorted[middle];
    },
    needsValue: true,
  },
  min: {
    labelKey: 'race.binTable.aggregations.min',
    fallback: 'Minimum',
    compute: (samples) => {
      if (samples.length === 0) {
        return null;
      }
      return Math.min(...samples);
    },
    needsValue: true,
  },
  max: {
    labelKey: 'race.binTable.aggregations.max',
    fallback: 'Maximum',
    compute: (samples) => {
      if (samples.length === 0) {
        return null;
      }
      return Math.max(...samples);
    },
    needsValue: true,
  },
  sum: {
    labelKey: 'race.binTable.aggregations.sum',
    fallback: 'Sum',
    compute: (samples) => {
      if (samples.length === 0) {
        return null;
      }
      return samples.reduce((acc, value) => acc + value, 0);
    },
    needsValue: true,
  },
  count: {
    labelKey: 'race.binTable.aggregations.count',
    fallback: 'Count',
    compute: (_samples, count) => count,
    needsValue: false,
  },
};

function getAccessibleCellTextColor(background: string): string {
  const contrastWithDark = chroma.contrast(background, DARK_CELL_TEXT_COLOR);
  const contrastWithLight = chroma.contrast(background, LIGHT_CELL_TEXT_COLOR);
  if (contrastWithDark >= MIN_CELL_TEXT_CONTRAST || contrastWithLight >= MIN_CELL_TEXT_CONTRAST) {
    return contrastWithDark >= contrastWithLight ? DARK_CELL_TEXT_COLOR : LIGHT_CELL_TEXT_COLOR;
  }
  return contrastWithDark >= contrastWithLight ? DARK_CELL_TEXT_COLOR : LIGHT_CELL_TEXT_COLOR;
}

function coerceNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      return null;
    }
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function getMetricValue(point: TrackPoint, metric: string): number | null {
  return coerceNumber((point as unknown as Record<string, unknown>)[metric]);
}

function listNumericMetrics(trackData: TrackPoint[]): string[] {
  const metrics = new Set<string>();
  for (const point of trackData) {
    for (const [key, raw] of Object.entries(point)) {
      if (RESERVED_METRIC_KEYS.has(key)) {
        continue;
      }
      if (coerceNumber(raw) !== null) {
        metrics.add(key);
      }
    }
  }
  const ordered = [...metrics];
  ordered.sort((a, b) => a.localeCompare(b));
  return ordered;
}

function createAutoBoundaries(values: number[], requestedBins: number): number[] {
  if (values.length === 0) {
    return [];
  }
  const sorted = [...values].sort((a, b) => a - b);
  const min = sorted.at(0);
  const max = sorted.at(-1);
  if (min === undefined || max === undefined || !Number.isFinite(min) || !Number.isFinite(max)) {
    return [];
  }
  const clampedBins = Math.max(
    MIN_BIN_COUNT,
    Math.min(MAX_BIN_COUNT, Math.floor(Number.isFinite(requestedBins) ? requestedBins : MIN_BIN_COUNT)),
  );
  if (min === max) {
    const epsilon = Math.abs(min) > 1 ? Math.abs(min) * 0.05 : 0.5;
    return [min - epsilon, min + epsilon];
  }
  const step = (max - min) / clampedBins;
  const boundaries: number[] = [];
  for (let index = 0; index <= clampedBins; index++) {
    if (index === 0) {
      boundaries.push(min);
      continue;
    }
    if (index === clampedBins) {
      boundaries.push(max);
      continue;
    }
    boundaries.push(min + step * index);
  }
  return boundaries;
}

function parseCustomBoundaries(input: string): { boundaries: number[] | null; error: AxisError | null } {
  const clean = input.replaceAll(';', ',').replaceAll('|', ',');
  const tokens = clean
    .split(/[\s,]+/)
    .map((token) => token.trim())
    .filter((token) => token.length > 0);
  if (tokens.length < 2) {
    return { boundaries: null, error: AXIS_ERROR_NOT_ENOUGH };
  }
  const parsed: number[] = [];
  for (const token of tokens) {
    const value = Number(token);
    if (!Number.isFinite(value)) {
      return { boundaries: null, error: AXIS_ERROR_INVALID };
    }
    parsed.push(value);
  }
  if (parsed.length < 2) {
    return { boundaries: null, error: AXIS_ERROR_NOT_ENOUGH };
  }
  for (let index = 1; index < parsed.length; index += 1) {
    if (parsed[index] <= parsed[index - 1]) {
      return { boundaries: null, error: AXIS_ERROR_INVALID };
    }
  }
  const capped = parsed.slice(0, MAX_BOUNDARIES + 1);
  return { boundaries: capped, error: null };
}

function computeAxisRanges(values: number[], config: AxisConfig): AxisComputation {
  if (values.length === 0) {
    return { ranges: [], error: AXIS_ERROR_NO_DATA };
  }

  let boundaries: number[] | null = null;
  if (config.mode === 'boundaries') {
    const { boundaries: parsed, error } = parseCustomBoundaries(config.boundariesText);
    if (error) {
      return { ranges: [], error };
    }
    boundaries = parsed;
  } else {
    boundaries = createAutoBoundaries(values, config.binCount);
  }

  if (!boundaries || boundaries.length < 2) {
    return { ranges: [], error: AXIS_ERROR_NOT_ENOUGH };
  }

  const capped = boundaries.slice(0, MAX_BOUNDARIES + 1);
  const ranges: BinRange[] = [];

  for (const [index, start] of capped.entries()) {
    if (index >= capped.length - 1) {
      break;
    }
    const end = capped[index + 1];
    if (end === undefined) {
      break;
    }
    if (!Number.isFinite(start) || !Number.isFinite(end)) {
      return { ranges: [], error: AXIS_ERROR_INVALID };
    }
    if (end <= start) {
      return { ranges: [], error: AXIS_ERROR_INVALID };
    }
    const inclusiveEnd = index === capped.length - 2;
    ranges.push({ start, end, inclusiveEnd });
  }

  return { ranges, error: ranges.length === 0 ? AXIS_ERROR_INVALID : null };
}

function findRangeIndex(value: number, ranges: BinRange[]): number {
  for (const [index, range] of ranges.entries()) {
    const { start, end, inclusiveEnd } = range;
    const lowerOk = value >= start;
    const upperOk = inclusiveEnd ? value <= end : value < end;
    if (lowerOk && upperOk) {
      return index;
    }
  }
  return -1;
}

function formatRangeLabel(range: BinRange, formatter: Intl.NumberFormat): string {
  const start = formatter.format(range.start);
  const end = formatter.format(range.end);
  const closing = range.inclusiveEnd ? ']' : ')';
  return `[${start}, ${end}${closing}`;
}

export interface BinTablePanelProperties {
  trackData: TrackPoint[];
}

export const BinTablePanel: React.FC<BinTablePanelProperties> = ({ trackData }) => {
  const { t } = useTranslation('common');

  const numberFormatter = useMemo(
    () =>
      new Intl.NumberFormat(undefined, {
        maximumFractionDigits: 2,
      }),
    [],
  );
  const integerFormatter = useMemo(
    () =>
      new Intl.NumberFormat(undefined, {
        maximumFractionDigits: 0,
      }),
    [],
  );

  const availableMetrics = useMemo(() => listNumericMetrics(trackData), [trackData]);
  const defaultColumnMetric = availableMetrics[0] ?? null;
  const defaultRowMetric =
    availableMetrics.find((metric) => metric !== defaultColumnMetric) ?? availableMetrics[0] ?? null;
  const defaultValueMetric =
    availableMetrics.find(
      (metric) => metric !== defaultColumnMetric && metric !== defaultRowMetric,
    ) ?? availableMetrics[0] ?? null;

  const [columnConfig, setColumnConfig] = useState<AxisConfig>(() => ({
    metric: defaultColumnMetric,
    mode: 'count',
    binCount: 10,
    boundariesText: '',
  }));
  const [rowConfig, setRowConfig] = useState<AxisConfig>(() => ({
    metric: defaultRowMetric,
    mode: 'count',
    binCount: 10,
    boundariesText: '',
  }));
  const [valueMetric, setValueMetric] = useState<string | null>(defaultValueMetric);
  const [aggregation, setAggregation] = useState<AggregationKey>('mean');
  const [minSamplesPerBin, setMinSamplesPerBin] = useState<number>(1);
  const [settingsOpen, setSettingsOpen] = useState<boolean>(false);
  const { height: viewportHeight } = useViewportSize();

  useEffect(() => {
    setColumnConfig((current) => {
      if (current.metric && availableMetrics.includes(current.metric)) {
        return current;
      }
      const fallback = availableMetrics[0] ?? null;
      if (fallback === current.metric) {
        return current;
      }
      return { ...current, metric: fallback };
    });
  }, [availableMetrics]);

  useEffect(() => {
    setRowConfig((current) => {
      if (current.metric && availableMetrics.includes(current.metric)) {
        return current;
      }
      const fallback =
        availableMetrics.find((metric) => metric !== (columnConfig.metric ?? '')) ??
        availableMetrics[0] ??
        null;
      if (fallback === current.metric) {
        return current;
      }
      return { ...current, metric: fallback };
    });
  }, [availableMetrics, columnConfig.metric]);

  useEffect(() => {
    setValueMetric((current) => {
      if (current && availableMetrics.includes(current)) {
        return current;
      }
      const fallback =
        availableMetrics.find(
          (metric) =>
            metric !== (columnConfig.metric ?? '') && metric !== (rowConfig.metric ?? ''),
        ) ?? availableMetrics[0] ?? null;
      if (fallback === current) {
        return current;
      }
      return fallback;
    });
  }, [availableMetrics, columnConfig.metric, rowConfig.metric]);

  const columnAxis = useMemo(() => {
    if (!columnConfig.metric) {
      return { ranges: [], error: AXIS_ERROR_NO_DATA };
    }
    const values = trackData
      .map((point) => getMetricValue(point, columnConfig.metric ?? ''))
      .filter((value): value is number => value !== null);
    return computeAxisRanges(values, columnConfig);
  }, [trackData, columnConfig]);

  const rowAxis = useMemo(() => {
    if (!rowConfig.metric) {
      return { ranges: [], error: AXIS_ERROR_NO_DATA };
    }
    const values = trackData
      .map((point) => getMetricValue(point, rowConfig.metric ?? ''))
      .filter((value): value is number => value !== null);
    return computeAxisRanges(values, rowConfig);
  }, [trackData, rowConfig]);

  const aggregationDefinition = AGGREGATIONS[aggregation];

  const heatmap = useMemo(() => {
    const empty = {
      cells: [] as CellComputation[][],
      columnRanges: columnAxis.ranges,
      rowRanges: rowAxis.ranges,
      domain: null as [number, number] | null,
      consideredPoints: 0,
    };

    if (!columnConfig.metric || !rowConfig.metric) {
      return empty;
    }
    if (columnAxis.error || rowAxis.error) {
      return empty;
    }
    if (aggregationDefinition.needsValue && !valueMetric) {
      return empty;
    }

    const matrix = rowAxis.ranges.map(() =>
      columnAxis.ranges.map(() => ({
        samples: [] as number[],
        count: 0,
      })),
    );

    let consideredPoints = 0;

    for (const point of trackData) {
      const columnValue = getMetricValue(point, columnConfig.metric);
      const rowValue = getMetricValue(point, rowConfig.metric);
      if (columnValue === null || rowValue === null) {
        continue;
      }
      const columnIndex = findRangeIndex(columnValue, columnAxis.ranges);
      const rowIndex = findRangeIndex(rowValue, rowAxis.ranges);
      if (columnIndex === -1 || rowIndex === -1) {
        continue;
      }
      const cell = matrix[rowIndex][columnIndex];
      if (aggregationDefinition.needsValue) {
        const metricValue = valueMetric ? getMetricValue(point, valueMetric) : null;
        if (metricValue === null) {
          continue;
        }
        cell.samples.push(metricValue);
      }
      cell.count += 1;
      consideredPoints += 1;
    }

    const cells = matrix.map((row) =>
      row.map((cell) => {
        if (cell.count < minSamplesPerBin) {
          return { value: null, count: cell.count };
        }
        const aggregated = aggregationDefinition.compute(cell.samples, cell.count);
        if (aggregated === null || Number.isNaN(aggregated)) {
          return { value: null, count: cell.count };
        }
        return { value: aggregated, count: cell.count };
      }),
    );

    const nonNullValues = cells
      .flat()
      .map((cell) => cell.value)
      .filter((value): value is number => value !== null);

    const domain =
      nonNullValues.length > 0
        ? ([Math.min(...nonNullValues), Math.max(...nonNullValues)] as [number, number])
        : null;

    return {
      cells,
      columnRanges: columnAxis.ranges,
      rowRanges: rowAxis.ranges,
      domain,
      consideredPoints,
    };
  }, [
    trackData,
    columnConfig.metric,
    rowConfig.metric,
    valueMetric,
    columnAxis,
    rowAxis,
    aggregationDefinition,
    minSamplesPerBin,
  ]);

  const colorPalette = useMemo(() => ['#f5f3ff', '#a855f7', '#4c1d95'], []);

  const colorScale = useMemo(() => {
    if (!heatmap.domain) {
      return null;
    }
    const [min, max] = heatmap.domain;
    if (!Number.isFinite(min) || !Number.isFinite(max)) {
      return null;
    }
    const adjustedMax = min === max ? min + 1 : max;
    return chroma.scale(colorPalette).mode('lab').domain([min, adjustedMax]);
  }, [heatmap.domain, colorPalette]);

  const metricOptions = useMemo(
    () =>
      availableMetrics.map((metric) => ({
        value: metric,
        label: formatMetricLabel(metric),
      })),
    [availableMetrics],
  );

  const aggregationOptions = useMemo(
    () =>
      (Object.keys(AGGREGATIONS) as AggregationKey[]).map((key) => ({
        value: key,
        label: t(AGGREGATIONS[key].labelKey, { defaultValue: AGGREGATIONS[key].fallback }),
      })),
    [t],
  );

  const modeOptions = useMemo(
    () => [
      {
        label: t('race.binTable.controls.mode.binCount', 'Bin Count'),
        value: 'count',
      },
      {
        label: t('race.binTable.controls.mode.binBoundaries', 'Bin Boundaries'),
        value: 'boundaries',
      },
    ],
    [t],
  );

  const errorMessages: Record<AxisError, string> = useMemo(
    () => ({
      [AXIS_ERROR_NO_DATA]: t('race.binTable.errors.noData', 'No numeric data for this metric'),
      [AXIS_ERROR_NOT_ENOUGH]: t(
        'race.binTable.errors.notEnoughBoundaries',
        'Provide at least two boundaries',
      ),
      [AXIS_ERROR_INVALID]: t(
        'race.binTable.errors.invalidBoundaries',
        'Boundaries must be numeric and increasing',
      ),
    }),
    [t],
  );

  const hasRenderableGrid =
    columnAxis.error === null &&
    rowAxis.error === null &&
    columnAxis.ranges.length > 0 &&
    rowAxis.ranges.length > 0 &&
    (!aggregationDefinition.needsValue || valueMetric);

  const hasAnyValue = heatmap.cells.some((row) => row.some((cell) => cell.value !== null));

  const columnMetricLabel = columnConfig.metric ? formatMetricLabel(columnConfig.metric) : '';
  const rowMetricLabel = rowConfig.metric ? formatMetricLabel(rowConfig.metric) : '';
  const valueMetricLabel = valueMetric ? formatMetricLabel(valueMetric) : '';

  useEffect(() => {
    if ((columnAxis.error || rowAxis.error) && !settingsOpen) {
      setSettingsOpen(true);
    }
  }, [columnAxis.error, rowAxis.error, settingsOpen]);

  const summaryFormattedCount = integerFormatter.format(heatmap.consideredPoints);
  const summary = t('race.binTable.summary', {
    formattedCount: summaryFormattedCount,
  });

  const metricLabelText = t('race.binTable.controls.metricLabel', 'Metric');
  const metricPlaceholderText = t('race.binTable.controls.metricPlaceholder', 'Select metric');
  const metricFallbackLabel = t('race.binTable.controls.metric', 'Metric');
  const binCountLabel = t('race.binTable.controls.binCount', 'Number of bins');
  const boundariesLabelText = t('race.binTable.controls.boundaries', 'Custom boundaries');
  const boundariesPlaceholderText = t(
    'race.binTable.controls.boundariesPlaceholder',
    'Example: 0, 5, 10, 15',
  );

  const gridTemplateColumns = `minmax(120px, 1fr) repeat(${columnAxis.ranges.length}, minmax(108px, 1fr))`;
  const gridTemplateRows = `auto repeat(${rowAxis.ranges.length}, minmax(68px, 1fr))`;
  const settingsMaxHeight = useMemo(() => {
    const safeHeight =
      Number.isFinite(viewportHeight) && viewportHeight > 0 ? viewportHeight : 720;
    const budget = safeHeight - 260;
    return Math.max(240, Math.min(560, budget));
  }, [viewportHeight]);
  const settingsScrollAreaProps: Partial<ScrollAreaProps> = settingsOpen
    ? {}
    : {
        mah: settingsMaxHeight,
        style: { maxHeight: settingsMaxHeight } as React.CSSProperties,
      };

  return (
    <Card
      className="bin-table-panel theme-inverted"
      withBorder
      radius="md"
      p="md"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minWidth: PANEL_MIN_WIDTH,
        minHeight: PANEL_MIN_HEIGHT,
        overflow: settingsOpen ? 'visible' : 'hidden',
      }}
      data-settings-open={settingsOpen || undefined}
    >
      <Stack gap="md" className="bin-table-panel__content">
        {settingsOpen ? (
          <Card withBorder padding="md" radius="lg" className="bin-table-settings-card">
            <Stack gap="sm">
              <Group justify="space-between" align="center">
                <Title order={4}>{t('race.binTable.title', 'Bin Table Settings')}</Title>
                <Button
                  size="xs"
                  variant="subtle"
                  onClick={() => setSettingsOpen(false)}
                  className="side-panel-button bin-table-settings-toggle"
                  data-open
                >
                  {t('race.binTable.controls.hideSettings', 'Hide settings')}
                </Button>
              </Group>
              <ScrollArea
                className="bin-table-settings-scroll"
                type="hover"
                offsetScrollbars
                scrollbarSize={10}
                data-expanded
                {...settingsScrollAreaProps}
              >
                <div className="bin-table-settings-grid">
                  <Card withBorder padding="sm" radius="md" className="bin-table-axis-card">
                    <Stack gap="xs">
                      <Text className="bin-table-section-title">
                        {t('race.binTable.controls.columns', {
                          metric: columnMetricLabel || metricFallbackLabel,
                        })}
                      </Text>
                      <Select
                        data={metricOptions}
                        value={columnConfig.metric}
                        onChange={(metric) => setColumnConfig((current) => ({ ...current, metric }))}
                        label={metricLabelText}
                        placeholder={metricPlaceholderText}
                        searchable
                      />
                      <SegmentedControl
                        fullWidth
                        value={columnConfig.mode}
                        onChange={(mode) =>
                          setColumnConfig((current) => ({
                            ...current,
                            mode: mode as BinMode,
                          }))
                        }
                        data={modeOptions}
                        size="xs"
                      />
                      {columnConfig.mode === 'count' ? (
                        <NumberInput
                          label={binCountLabel}
                          value={columnConfig.binCount}
                          onChange={(value) =>
                            setColumnConfig((current) => ({
                              ...current,
                              binCount:
                                typeof value === 'number'
                                  ? Math.max(
                                      MIN_BIN_COUNT,
                                      Math.min(MAX_BIN_COUNT, Math.round(value)),
                                    )
                                  : current.binCount,
                            }))
                          }
                          min={MIN_BIN_COUNT}
                          max={MAX_BIN_COUNT}
                          step={1}
                        />
                      ) : (
                        <TextInput
                          label={boundariesLabelText}
                          placeholder={boundariesPlaceholderText}
                          value={columnConfig.boundariesText}
                          onChange={(event) =>
                            setColumnConfig((current) => ({
                              ...current,
                              boundariesText: event.currentTarget.value,
                            }))
                          }
                        />
                      )}
                      {columnAxis.error && (
                        <Text className="bin-table-error" role="alert">
                          {errorMessages[columnAxis.error]}
                        </Text>
                      )}
                    </Stack>
                  </Card>

                  <Card withBorder padding="sm" radius="md" className="bin-table-axis-card">
                    <Stack gap="xs">
                      <Text className="bin-table-section-title">
                        {t('race.binTable.controls.rows', {
                          metric: rowMetricLabel || metricFallbackLabel,
                        })}
                      </Text>
                      <Select
                        data={metricOptions}
                        value={rowConfig.metric}
                        onChange={(metric) => setRowConfig((current) => ({ ...current, metric }))}
                        label={metricLabelText}
                        placeholder={metricPlaceholderText}
                        searchable
                      />
                      <SegmentedControl
                        fullWidth
                        value={rowConfig.mode}
                        onChange={(mode) =>
                          setRowConfig((current) => ({
                            ...current,
                            mode: mode as BinMode,
                          }))
                        }
                        data={modeOptions}
                        size="xs"
                      />
                      {rowConfig.mode === 'count' ? (
                        <NumberInput
                          label={binCountLabel}
                          value={rowConfig.binCount}
                          onChange={(value) =>
                            setRowConfig((current) => ({
                              ...current,
                              binCount:
                                typeof value === 'number'
                                  ? Math.max(
                                      MIN_BIN_COUNT,
                                      Math.min(MAX_BIN_COUNT, Math.round(value)),
                                    )
                                  : current.binCount,
                            }))
                          }
                          min={MIN_BIN_COUNT}
                          max={MAX_BIN_COUNT}
                          step={1}
                        />
                      ) : (
                        <TextInput
                          label={boundariesLabelText}
                          placeholder={boundariesPlaceholderText}
                          value={rowConfig.boundariesText}
                          onChange={(event) =>
                            setRowConfig((current) => ({
                              ...current,
                              boundariesText: event.currentTarget.value,
                            }))
                          }
                        />
                      )}
                      {rowAxis.error && (
                        <Text className="bin-table-error" role="alert">
                          {errorMessages[rowAxis.error]}
                        </Text>
                      )}
                    </Stack>
                  </Card>

                  <Card withBorder padding="sm" radius="md" className="bin-table-metrics-card">
                    <Stack gap="xs">
                      <Text className="bin-table-section-title">
                        {t('race.binTable.controls.valuesSection', 'Values')}
                      </Text>
                      <Select
                        data={metricOptions}
                        value={valueMetric}
                        onChange={setValueMetric}
                        label={t('race.binTable.controls.valueMetric', 'Value metric')}
                        placeholder={metricPlaceholderText}
                        searchable
                        disabled={!aggregationDefinition.needsValue}
                      />
                      <Select
                        data={aggregationOptions}
                        value={aggregation}
                        onChange={(value) => setAggregation(value as AggregationKey)}
                        label={t('race.binTable.controls.aggregation', 'Aggregation')}
                      />
                      <NumberInput
                        label={t('race.binTable.controls.minPoints', 'Minimum samples per bin')}
                        value={minSamplesPerBin}
                        min={1}
                        max={999}
                        step={1}
                        onChange={(value) =>
                          setMinSamplesPerBin((current) =>
                            typeof value === 'number' ? Math.max(1, Math.round(value)) : current,
                          )
                        }
                      />
                    </Stack>
                  </Card>
                </div>
              </ScrollArea>
            </Stack>
          </Card>
        ) : (
          <Group justify="space-between" align="center" className="bin-table-settings-toolbar">
            <Title order={5}>{t('race.binTable.title', 'Bin Table Settings')}</Title>
            <Button
              size="xs"
              variant="light"
              onClick={() => setSettingsOpen(true)}
              className="side-panel-button bin-table-settings-toggle"
            >
              {t('race.binTable.controls.showSettings', 'Show settings')}
            </Button>
          </Group>
        )}

      <Card withBorder padding="md" radius="lg" className="bin-table-heatmap-card">
        <Stack gap="sm" className="bin-table-heatmap-wrapper">
          <Group justify="space-between" align="baseline">
            <div>
              <Title order={5}>{t('race.binTable.heatmap.title', 'Aggregated Heatmap')}</Title>
              <Text size="xs" color="dimmed">
                {summary}
              </Text>
            </div>
            <Stack gap={2} align="flex-end">
              <Text size="xs" color="dimmed">
                {t('race.binTable.heatmap.columnsLabel', { metric: columnMetricLabel })}
              </Text>
              <Text size="xs" color="dimmed">
                {t('race.binTable.heatmap.rowsLabel', { metric: rowMetricLabel })}
              </Text>
              {aggregationDefinition.needsValue && (
                <Text size="xs" color="dimmed">
                  {t('race.binTable.heatmap.valuesLabel', { metric: valueMetricLabel })}
                </Text>
              )}
            </Stack>
          </Group>

          {hasRenderableGrid ? (
            <div className="bin-table-heatmap-scroll" style={{ flex: '1 1 auto', minHeight: 0 }}>
              <Box
                className="bin-table-grid"
                role="grid"
                aria-label={t('race.binTable.heatmap.ariaLabel', 'Bin aggregation table')}
                style={{
                  gridTemplateColumns,
                  gridTemplateRows,
                }}
              >
                <div className="bin-table-grid-corner" role="columnheader">
                  {t('race.binTable.heatmap.cornerLabel', 'Rows / Columns')}
                </div>
                {columnAxis.ranges.map((range, index) => (
                  <div
                    key={`col-header-${index}`}
                    className="bin-table-grid-header"
                    role="columnheader"
                  >
                    {formatRangeLabel(range, numberFormatter)}
                  </div>
                ))}
                {rowAxis.ranges.map((rowRange, rowIndex) => (
                  <Fragment key={`row-${rowIndex}`}>
                    <div className="bin-table-grid-row-label" role="rowheader">
                      {formatRangeLabel(rowRange, numberFormatter)}
                    </div>
                    {columnAxis.ranges.map((colRange, colIndex) => {
                      const cell = heatmap.cells[rowIndex]?.[colIndex] ?? { value: null, count: 0 };
                      const numericValue = cell.value ?? undefined;
                      const hasValue = typeof numericValue === 'number';
                      const background =
                        hasValue && colorScale
                          ? colorScale(numericValue).hex()
                          : 'var(--inverted-surface-muted, rgba(250, 253, 255, 0.92))';
                      const textColor =
                        hasValue && colorScale
                          ? getAccessibleCellTextColor(background)
                          : DARK_CELL_TEXT_COLOR;
                      const formattedValue = hasValue
                        ? numberFormatter.format(numericValue as number)
                        : null;
                      const ariaValue =
                        formattedValue ?? t('race.binTable.heatmap.noValue', 'No value');
                      const displayValue =
                        formattedValue ?? t('race.binTable.heatmap.placeholder', '--');
                      return (
                        <div
                          key={`cell-${rowIndex}-${colIndex}`}
                          className="bin-table-grid-cell"
                          role="gridcell"
                          aria-label={t('race.binTable.heatmap.cellLabel', {
                            column: formatRangeLabel(colRange, numberFormatter),
                            row: formatRangeLabel(rowRange, numberFormatter),
                            value: ariaValue,
                            count: cell.count,
                          })}
                          style={{
                            background,
                            color: textColor,
                          }}
                        >
                          <span className="bin-table-grid-value">{displayValue}</span>
                          <span className="bin-table-grid-count">
                            {t('race.binTable.heatmap.countShort', {
                              formattedCount: integerFormatter.format(cell.count),
                            })}
                          </span>
                        </div>
                      );
                    })}
                  </Fragment>
                ))}
              </Box>
            </div>
          ) : (
            <Box className="bin-table-empty-state" role="status">
              <Text>
                {t(
                  'race.binTable.heatmap.configure',
                  'Select numeric metrics and valid bins to display the table.',
                )}
              </Text>
            </Box>
          )}

          {hasRenderableGrid && !hasAnyValue && (
            <Box className="bin-table-empty-notice" role="status">
              <Text size="sm" color="dimmed">
                {t('race.binTable.heatmap.empty', 'No bins meet the criteria yet.')}
              </Text>
            </Box>
          )}
        </Stack>
      </Card>
    </Stack>
  </Card>
);
};
